import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import SubnoteDataTester from '../components/SubnoteDataTester'
import toast from 'react-hot-toast'

const DebugSubnotes = () => {
  const { id: alertId } = useParams()
  const { getAlertSubnotes, testFirestoreConnection, addAlertSubnote } = useFirebase()
  const [subnotes, setSubnotes] = useState([])
  const [loading, setLoading] = useState(false)
  const [debugInfo, setDebugInfo] = useState('')

  const loadSubnotesWithDebug = async () => {
    setLoading(true)
    setDebugInfo('')
    try {
      console.log('🔍 Debug: Starting to load subnotes for alertId:', alertId)
      
      // Add debug info
      let debug = `Alert ID: ${alertId}\n`
      debug += `Timestamp: ${new Date().toISOString()}\n`
      
      const subnotes = await getAlertSubnotes(alertId)
      console.log('🔍 Debug: Raw subnotes data:', subnotes)
      
      debug += `Subnotes count: ${subnotes?.length || 0}\n`
      debug += `Subnotes IDs: ${subnotes?.map(s => s.id).join(', ') || 'None'}\n`
      
      if (subnotes && subnotes.length > 0) {
        debug += `First subnote data: ${JSON.stringify(subnotes[0], null, 2)}\n`
      }
      
      setSubnotes(subnotes || [])
      setDebugInfo(debug)
      
      // Try to get data directly from Firestore as well
      console.log('🔍 Debug: Checking Firestore directly...')
      
    } catch (error) {
      console.error('🔍 Debug: Error loading subnotes:', error)
      setDebugInfo(`Error: ${error.message}`)
      toast.error('Failed to load subnotes: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const testCreateSubnote = async () => {
    try {
      console.log('🔍 Debug: Testing create subnote...')
      
      const testData = {
        title: 'Debug Test Subnote',
        message: `Created at ${new Date().toISOString()}`,
        createdBy: 'debug-test@example.com'
      }
      
      console.log('🔍 Debug: Creating subnote with data:', testData)
      
      const id = await addAlertSubnote(alertId, testData)
      console.log('🔍 Debug: Subnote created with ID:', id)
      
      toast.success(`Debug subnote created with ID: ${id}`)
      
      // Reload data
      setTimeout(() => {
        loadSubnotesWithDebug()
      }, 1000) // Wait a bit for Firestore to sync
      
    } catch (error) {
      console.error('🔍 Debug: Error creating test subnote:', error)
      toast.error('Failed to create test subnote: ' + error.message)
    }
  }

  useEffect(() => {
    if (alertId) {
      loadSubnotesWithDebug()
    }
  }, [alertId])

  return (
    <div className="p-6 space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h2 className="text-xl font-bold text-yellow-900 mb-4">Subnotes Debug Page</h2>
        
        <div className="mb-4">
          <p className="text-sm text-yellow-700 mb-2">
            Alert ID: <code className="bg-yellow-100 px-2 py-1 rounded">{alertId}</code>
          </p>
        </div>

        <div className="flex gap-2 mb-4">
          <button
            onClick={loadSubnotesWithDebug}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Load Subnotes (Debug)'}
          </button>
          
          <button
            onClick={testCreateSubnote}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Create Test Subnote
          </button>
        </div>

        {debugInfo && (
          <div className="mb-4">
            <h3 className="font-bold text-yellow-900 mb-2">Debug Info:</h3>
            <pre className="bg-yellow-100 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap">
              {debugInfo}
            </pre>
          </div>
        )}

        {subnotes.length > 0 && (
          <div className="mb-4">
            <h3 className="font-bold text-yellow-900 mb-2">Loaded Subnotes ({subnotes.length}):</h3>
            <div className="bg-white rounded border max-h-60 overflow-y-auto">
              {subnotes.map((subnote, index) => (
                <div key={subnote.id} className="p-3 border-b last:border-b-0">
                  <div className="text-sm">
                    <div className="font-medium text-gray-900">
                      {subnote.title || 'Untitled'}
                    </div>
                    <div className="text-gray-600 truncate">
                      {subnote.message?.substring(0, 100)}...
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      ID: {subnote.id} | 
                      {subnote.isFileContent ? ' File Content' : ' Text Content'} |
                      Created: {subnote.createdAt?.toLocaleDateString?.() || 'Unknown'}
                    </div>
                    <details className="mt-2">
                      <summary className="text-xs text-blue-600 cursor-pointer">Show raw data</summary>
                      <pre className="text-xs bg-gray-100 p-2 mt-1 rounded overflow-x-auto">
                        {JSON.stringify(subnote, null, 2)}
                      </pre>
                    </details>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Include the full test component */}
      <SubnoteDataTester alertId={alertId} />
    </div>
  )
}

export default DebugSubnotes